import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowRightIcon, ArrowsClockwiseIcon } from '@phosphor-icons/react';

interface CurrencyConversionCardProps {
  sourceCurrency: string;
  targetCurrency?: string;
  currentRate?: number;
  onApplyConversion: (rate: number) => void;
  disabled?: boolean;
  isApplying?: boolean;
  hasBeenApplied?: boolean;
  onRateChange?: (rate: number | undefined) => void; // Update constants as user types
}

const CurrencyConversionCard: React.FC<CurrencyConversionCardProps> = ({
  sourceCurrency,
  targetCurrency = 'CAD',
  currentRate,
  onApplyConversion,
  disabled = false,
  isApplying = false,
  hasBeenApplied = false,
  onRateChange,
}) => {
  const [rateInput, setRateInput] = useState<string>(
    currentRate?.toString() || '1.0',
  );
  const [error, setError] = useState<string | null>(null);

  // Sync input with currentRate when it changes (e.g., after auto-apply)
  useEffect(() => {
    if (currentRate !== undefined) {
      setRateInput(currentRate.toString());
    }
  }, [currentRate]);

  const handleRateChange = useCallback(
    (value: string) => {
      setRateInput(value);
      setError(null);

      const numericValue = parseFloat(value);

      // Update constants immediately as user types (so auto-apply uses latest value)
      if (onRateChange) {
        if (!isNaN(numericValue) && numericValue > 0) {
          onRateChange(numericValue);
        } else {
          onRateChange(undefined);
        }
      }
    },
    [onRateChange],
  );

  const handleApply = useCallback(() => {
    const rate = parseFloat(rateInput);

    if (isNaN(rate) || rate <= 0) {
      setError('Please enter a valid conversion rate greater than 0');
      return;
    }

    onApplyConversion(rate);
  }, [rateInput, onApplyConversion]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleApply();
      }
    },
    [handleApply],
  );

  const rateChanged = currentRate && parseFloat(rateInput) !== currentRate;

  // Don't show if source and target are the same
  if (sourceCurrency === targetCurrency) {
    return null;
  }

  return (
    <Card className="mb-8">
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-1">
                Currency Conversion
              </h3>
              <p className="text-sm text-gray-600">
                Convert values from {sourceCurrency} to {targetCurrency}
              </p>
            </div>
            {hasBeenApplied && !rateChanged && (
              <Badge
                variant="secondary"
                className="bg-green-100 text-green-700"
              >
                ✓ Conversion Applied
              </Badge>
            )}
            {hasBeenApplied && rateChanged && (
              <Badge
                variant="secondary"
                className="bg-orange-100 text-orange-700"
              >
                ⚠ Rate Changed
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{sourceCurrency}</span>
              <ArrowRightIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">{targetCurrency}</span>
            </div>

            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm text-gray-600">Rate:</span>
              <Input
                type="number"
                step="0.0001"
                min="0"
                placeholder="1.0"
                value={rateInput}
                onChange={(e) => handleRateChange(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={disabled || isApplying}
                className="w-32"
              />
              <span className="text-sm text-gray-500">
                (1 {sourceCurrency} = {rateInput || '1.0'} {targetCurrency})
              </span>
            </div>

            <Button
              onClick={handleApply}
              disabled={
                disabled ||
                isApplying ||
                !rateInput ||
                parseFloat(rateInput) <= 0
              }
              variant={rateChanged ? 'default' : 'outline'}
              className={
                rateChanged
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : ''
              }
            >
              {isApplying ? (
                <>
                  <ArrowsClockwiseIcon className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : rateChanged ? (
                <>
                  <ArrowsClockwiseIcon className="w-4 h-4 mr-2" />
                  Reapply Conversion
                </>
              ) : (
                'Apply Conversion'
              )}
            </Button>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {hasBeenApplied && (
            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-200">
              <p className="font-medium mb-1">✓ Conversion Applied</p>
              <p className="mt-1">Your output now includes:</p>
              <ul className="mt-1 ml-4 space-y-0.5">
                <li>
                  • <strong>Value {sourceCurrency}</strong> - Original values in{' '}
                  {sourceCurrency}
                </li>
                <li>
                  • <strong>Conversion Rate to {targetCurrency}</strong> - Rate
                  used ({currentRate})
                </li>
                <li>
                  • <strong>Value</strong> - Converted values in{' '}
                  {targetCurrency} (rounded to 2 decimals)
                </li>
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CurrencyConversionCard;
